import { env } from '@/config/env';

// Use environment variables or fallback to defaults
const supabaseUrl = env.VITE_SUPABASE_URL || 'https://lcevfqxsypgtrquujsug.supabase.co';
const supabaseAnonKey = env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxjZXZmcXhzeXBndHJxdXVqc3VnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDMxNTY3OTAsImV4cCI6MjA1ODczMjc5MH0.j_PXFHgREbIFcR_AuPHzTFvf0HKM66jKoGq57JyP-yM';

// Define allowed domains for the application
const ALLOWED_DOMAINS = [
  'www.coachvault.co.uk',
  'coachvault.co.uk',
  'coach-vault.vercel.app',
  'coaching-logbook-qfm8yufv9-simon-makins-projects-e19ad539.vercel.app'
];

// Ensure we have the correct app domain with proper formatting
let APP_DOMAIN = env.VITE_APP_URL || 'https://www.coachvault.co.uk';
// Remove trailing slash if present
if (APP_DOMAIN.endsWith('/')) {
  APP_DOMAIN = APP_DOMAIN.slice(0, -1);
}

// Get the current domain in browser environment
const getCurrentDomain = () => {
  if (typeof window === 'undefined') return APP_DOMAIN;
  return window.location.origin;
};

// Check if a URL is on an allowed domain
const isAllowedDomain = (url: string) => {
  try {
    const urlObj = new URL(url);
    return ALLOWED_DOMAINS.some(domain => urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`));
  } catch (e) {
    return false;
  }
};

/**
 * Direct API interface for Supabase Auth Service
 * This bypasses the Supabase SDK and directly calls the REST API
 */
export const supabaseDirectApi = {
  /**
   * Send a password reset email
   * This directly calls the Supabase Auth API without using the SDK
   */
  resetPassword: async (email: string, options?: { redirectTo?: string }) => {
    try {
      // Ensure we have a properly formatted redirect URL using our app domain
      let redirectTo = options?.redirectTo || `${APP_DOMAIN}/reset-password`;
      
      // Ensure the redirect URL is absolute and properly formatted
      if (!redirectTo.startsWith('http')) {
        redirectTo = `${APP_DOMAIN}${redirectTo.startsWith('/') ? '' : '/'}${redirectTo}`;
      }
      
      console.log('[Direct API] Sending reset email to:', email);
      console.log('[Direct API] Using redirect URL:', redirectTo);
      
      // Build request URL to Supabase auth API directly
      const resetUrl = `${supabaseUrl}/auth/v1/recover`;
      
      // Make the request directly to the Supabase Auth API
      const response = await fetch(resetUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseAnonKey,
          'X-Client-Info': 'coach-vault-app'
        },
        body: JSON.stringify({
          email,
          redirect_to: redirectTo
        })
      });
      
      // Handle success case
      if (response.ok) {
        console.log('[Direct API] Password reset email sent successfully');
        return { data: {}, error: null };
      } 
      
      // Handle error responses
      const errorData = await response.json().catch(() => ({}));
      console.error('[Direct API] Error response:', errorData);
      
      return { 
        data: null,
        error: {
          message: errorData.message || errorData.error_description || 'Failed to send password reset email',
          status: response.status,
          name: errorData.error || 'ApiError'
        }
      };
    } catch (error) {
      console.error('[Direct API] Error sending reset email:', error);
      return { 
        data: null, 
        error: { 
          message: error instanceof Error ? error.message : 'Unknown error occurred',
          status: 500,
          name: 'NetworkError'
        }
      };
    }
  },
  
  /**
   * Handle token redirect for password reset and other auth flows
   * This can be called manually or from useEffect to ensure proper redirection
   */
  handleTokenRedirect: () => {
    // Only run in browser environment
    if (typeof window === 'undefined') return;
    
    try {
      // Parse URL more robustly to detect various token formats
      const currentUrl = window.location.href;
      const urlObj = new URL(currentUrl);
      
      // Handle hash parameters (access_token, etc)
      const hashParams = new URLSearchParams(
        urlObj.hash.startsWith('#') ? urlObj.hash.substring(1) : urlObj.hash
      );
      
      // Check for all possible token patterns
      const hasAccessToken = hashParams.has('access_token') || urlObj.hash.includes('access_token=');
      const hasRecoveryType = 
        (hashParams.has('type') && hashParams.get('type') === 'recovery') || 
        urlObj.hash.includes('type=recovery') || 
        urlObj.search.includes('type=recovery');
      const hasToken = urlObj.searchParams.has('token');
      const hasCodeParam = urlObj.searchParams.has('code');
      
      // Extract any error information
      const hashError = hashParams.get('error') || hashParams.get('error_description');
      const queryError = urlObj.searchParams.get('error') || urlObj.searchParams.get('error_description');
      const hasError = !!hashError || !!queryError;
      
      // Enhanced logging for debugging
      console.log('[Direct API] URL analysis:', {
        currentUrl,
        hasAccessToken,
        hasRecoveryType,
        hasToken,
        hasCodeParam,
        pathname: urlObj.pathname,
        hash: urlObj.hash,
        search: urlObj.search,
        origin: urlObj.origin
      });
      
      // Only redirect if we're not already on the reset-password page
      const isOnResetPage = urlObj.pathname.includes('reset-password');
      const isOnVerifyPage = urlObj.pathname.includes('verify');
      
      // Only redirect to reset-password for password reset/recovery flows,
      // and never if we are currently on /verify (let /verify handle codes)
      if (!isOnResetPage && !isOnVerifyPage && (hasAccessToken || hasRecoveryType)) {
        console.log('[Direct API] Detected recovery auth token in URL, redirecting to reset-password page');
        const currentDomain = urlObj.origin;
        const resetPasswordUrl = `${currentDomain}/reset-password`;
        let redirectUrl = `${resetPasswordUrl}${urlObj.hash}`;
        console.log('[Direct API] Redirecting to:', redirectUrl);
        window.location.href = redirectUrl;
        return true; // Redirect initiated
      }
      // Do not redirect for regular /verify?code=... flows (email/PKCE verification)
      return false; // No redirect needed
    } catch (error) {
      console.error('[Direct API] Error handling token redirect:', error);
      return false;
    }
  },

  /**
   * Exchange a recovery token for a session directly via API
   * This is useful when the Supabase SDK isn't handling tokens correctly
   */
  exchangeTokenForSession: async (token: string) => {
    try {
      console.log('[Direct API] Attempting to exchange token for session');
      
      // Build request URL to Supabase auth API directly
      const tokenUrl = `${supabaseUrl}/auth/v1/token`;
      
      // Make the request directly to the Supabase Auth API
      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseAnonKey,
          'X-Client-Info': 'coach-vault-app'
        },
        body: JSON.stringify({
          grant_type: 'recovery',
          token
        })
      });
      
      // Handle success case
      if (response.ok) {
        const sessionData = await response.json();
        console.log('[Direct API] Token exchange successful');
        return { data: sessionData, error: null };
      } 
      
      // Handle error responses
      const errorData = await response.json().catch(() => ({}));
      console.error('[Direct API] Token exchange error:', errorData);
      
      return { 
        data: null,
        error: {
          message: errorData.message || errorData.error_description || 'Failed to exchange token',
          status: response.status,
          name: errorData.error || 'ApiError'
        }
      };
    } catch (error) {
      console.error('[Direct API] Error exchanging token:', error);
      return { 
        data: null, 
        error: { 
          message: error instanceof Error ? error.message : 'Unknown error occurred',
          status: 500,
          name: 'NetworkError'
        }
      };
    }
  },

  /**
   * Update user password directly via API using a reset token
   * This is a fallback for when the standard auth flow fails
   */
  updatePasswordWithResetToken: async (password: string, token: string) => {
    try {
      console.log('[Direct API] Attempting to update password with reset token');
      
      // Build request URL to Supabase auth API directly
      const updateUrl = `${supabaseUrl}/auth/v1/user`;
      
      // Make the request directly to the Supabase Auth API
      const response = await fetch(updateUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${token}`,
          'X-Client-Info': 'coach-vault-app'
        },
        body: JSON.stringify({
          password
        })
      });
      
      // Handle success case
      if (response.ok) {
        const userData = await response.json();
        console.log('[Direct API] Password update successful');
        return { data: userData, error: null };
      } 
      
      // Handle error responses
      const errorData = await response.json().catch(() => ({}));
      console.error('[Direct API] Password update error:', errorData);
      
      return { 
        data: null,
        error: {
          message: errorData.message || errorData.error_description || 'Failed to update password',
          status: response.status,
          name: errorData.error || 'ApiError'
        }
      };
    } catch (error) {
      console.error('[Direct API] Error updating password:', error);
      return { 
        data: null, 
        error: { 
          message: error instanceof Error ? error.message : 'Unknown error occurred',
          status: 500,
          name: 'NetworkError'
        }
      };
    }
  }
};

export default supabaseDirectApi; 